import React, { useEffect, useState } from 'react'
import Cropper from 'react-cropper'

import { Box, Stack, Theme, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge'

import ButtonComponent from './ButtonComponent'
import UploadMediaButton from './UploadMediaButton'
import MediaPreview from './MediaPreview'
import Preloader from './Preloader'
import { PreviewFormat } from '../types'

export interface RatioSelect {
  label: string
  ratio: number | undefined
}

interface IProps {
  previewFormat?: PreviewFormat
  mediaFile?: string | undefined
  mediaFileType?: 'image' | 'video' | undefined
  description?: string
  preview?: string | undefined
  getCropData: () => void
  ratioSelection?: RatioSelect[]
  openRationSelection?: boolean
  allowedExtensions?: string[]
  initialAspectRatio?: number
  aspectRatio?: number
  newImage?: boolean
  loading?: boolean
  deleteImage?: () => void
  onChangeImage?: (imageUrl: string, file: File | undefined) => void
  onChangeVideo?: (videoUrl: string, file: File | undefined) => void
  onFileUpload?: (fileUrl: string, file: File | undefined) => void
  allowVideoUpload?: boolean
  allowImageUpload?: boolean
  handleCropped: (instance: Cropper) => void
  minCropBoxHeight?: number
  minCropBoxWidth?: number
  cropBoxResizable?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  space: {
    margin: '0 0 30px 0',
  },
  ratioSelect: {
    width: '100px',
    background: 'rgba(0, 0, 0, 0.6)',
    color: '#FFF',
    borderRadius: theme.spacing(),
    padding: theme.spacing(),
    position: 'absolute',
    left: '75px',
    top: 0,
    zIndex: 100,
    cursor: 'pointer',
  },
}))

const MediaCropperComponent: React.FC<IProps> = (props) => {
  const classes = useStyles()
  const newImage = props?.newImage || false

  const previewFormat = props.previewFormat || PreviewFormat.square

  const [showRatioSelection, setShowRatioSelection] = useState<boolean>(props.openRationSelection || false)
  const [aspectRatio, setAspectRatio] = useState<number | undefined>(props.aspectRatio)
  const [cropper, setCropper] = useState<any>()
  const [imageUploadAllowed] = useState<boolean>(props.allowImageUpload ? props.allowImageUpload : true)

  const [preview, setPreview] = useState<string | undefined>(props.preview)

  useEffect(() => {
    setShowRatioSelection(props.openRationSelection || false)
  }, [props.openRationSelection])

  useEffect(() => {
    setPreview(props.preview)
  }, [props.preview])

  return (
    <>
      {props.description &&
        <Typography variant='body1' className={classes.space}>{props.description}</Typography>}
      {!props.loading ? (
        <>
          {!props.mediaFile && !preview &&
            <UploadMediaButton
              onImageUpload={(imageUrl, file) => {
                props.onChangeImage && props.onChangeImage(imageUrl, file)
              }}
              onVideoUpload={(videoUrl, file) => {
                props.onChangeVideo && props.onChangeVideo(videoUrl, file)
              }}
              allowVideoUpload={props.allowVideoUpload}
              allowImageUpload={imageUploadAllowed}
            />}
          {props.preview &&
            <MediaPreview
              view='row'
              previewFormat={previewFormat}
              src={preview}
              type={props.mediaFileType}
              deleteMediaFile={() => {
                props.deleteImage && props.deleteImage() // TODO replace with deleteMediaFile function
              }}
            />}
          {props.mediaFile && !preview && (
            <>
              <div className='previewImage' />
              <Cropper
                style={{ height: 400, width: '100%' }}
                className={classes.space}
                preview='.previewImage'
                src={props.mediaFile}
                aspectRatio={aspectRatio}
                initialAspectRatio={props.initialAspectRatio ? props.initialAspectRatio : aspectRatio}
                viewMode={1}
                guides
                minCropBoxHeight={props.minCropBoxHeight ? props.minCropBoxHeight : 10}
                minCropBoxWidth={props.minCropBoxWidth ? props.minCropBoxWidth : 10}
                cropBoxResizable={props.cropBoxResizable ? props.cropBoxResizable : true}
                scalable
                zoomable
                background
                responsive
                autoCropArea={1}
                checkOrientation={false}
                onInitialized={(instance: any) => {
                  setCropper(instance)
                  props.handleCropped(instance)
                }}
              />
              <Stack direction='row' justifyContent='space-between'>
                <Box sx={{ position: 'relative' }}>{(props.ratioSelection && !newImage) &&
                  <>
                    <ButtonComponent
                      size='small'
                      onClick={() => setShowRatioSelection(!showRatioSelection)}
                      text={<PhotoSizeSelectLargeIcon />}
                      disabled={!props.mediaFile}
                    />
                    {showRatioSelection &&
                      <div className={classes.ratioSelect}>
                        {props.ratioSelection.map((elem: RatioSelect, index: number) =>
                          <div
                            key={index}
                            onClick={() => {
                              if (cropper) {
                                // const originRatio = cropper.initialImageData.aspectRatio
                                if (elem.ratio) {
                                  setAspectRatio(elem.ratio)
                                  cropper.setAspectRatio(elem.ratio)
                                } else {
                                  setAspectRatio(undefined)
                                  cropper.setAspectRatio(null)
                                  // cropper.setInitialAspectRatio(originRatio)
                                }
                              }
                              setShowRatioSelection(!showRatioSelection)
                            }}
                          >
                            {elem.label}
                          </div>,
                        )}
                      </div>}
                  </>}
                </Box>
                <Box>
                  <ButtonComponent
                    color='primary'
                    onClick={() => {
                      props.getCropData && props.getCropData()
                    }}
                    text='Bereich auswählen'
                  />
                </Box>
              </Stack>
            </>)}
        </>
      ) : (<Preloader />)}
    </>
  )
}

export default MediaCropperComponent
